// ============================================================================
//  NhiaReportPanel — load the report NHIA returns, and act on it
// ============================================================================
//
//  Claims ▸ NHIA Import Report. Three things in one screen, because they are one
//  job: load the report, read what the scheme said, correct what can be
//  corrected.
//
//  Without this screen the rest of the feature is unreachable — the verdicts are
//  stored and the claim editor shows them, but nothing puts them there.
//
//  ── PREVIEW, THEN KEEP ─────────────────────────────────────────────────────
//
//  Reading a report stores nothing. That is a separate button, and the preview
//  reports how many of the report's claims this installation actually holds —
//  a report from another facility would match none, and finding that out AFTER
//  storing 4,515 verdicts would be a mess to unpick.
//
//  ── THE BULK FIX IS NOT A BUTTON ───────────────────────────────────────────
//
//  Two of the five change a patient's NAME and a MEMBERSHIP NUMBER. So the fix
//  is a table you read: every row shows the claim, the old value and the new one,
//  and rows the planner REFUSED are shown too, with the reason, because those are
//  work still outstanding rather than nothing.
//
//  Thirteen of the eighteen rules cannot be corrected mechanically. Each says so
//  and why, rather than offering a button that would do nothing — several depend
//  on facility credentialling this system does not hold, and two local
//  approximations of them were measured against a real report and were wrong by
//  33x and by 1,090 rows.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  UploadCloud, FileText, AlertTriangle, XCircle, CheckCircle2, Wrench,
  Info, Loader, ChevronRight, ChevronDown, RotateCcw, Wand2,
} from 'lucide-react';
import {
  nhiaReportPreview, nhiaReportImport, nhiaReportSummary, nhiaReportBatches,
  nhiaFixPreview, nhiaFixApply,
} from '../BaseURL';
import { BRAND } from '../../brand';

const TONE = {
  error: { bg: BRAND.dangerSoft, bd: 'rgba(166,40,40,.28)', fg: '#A62828' },
  warning: { bg: '#FFF4E5', bd: 'rgba(154,91,0,.28)', fg: '#7A4A00' },
  ok: { bg: '#E8F5EC', bd: 'rgba(30,107,57,.22)', fg: '#1E6B39' },
  plain: { bg: '#F1F5F9', bd: BRAND.line, fg: BRAND.inkMuted },
};

const Pill = ({ tone = 'plain', children, title }) => {
  const t = TONE[tone] || TONE.plain;
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '1px 8px', borderRadius: 999, fontSize: 10.5, fontWeight: 800,
      textTransform: 'uppercase', letterSpacing: .3, whiteSpace: 'nowrap',
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
    }}>{children}</span>
  );
};

const Stat = ({ label, value, tone = 'plain' }) => {
  const t = TONE[tone] || TONE.plain;
  return (
    <div style={{
      border: `1px solid ${t.bd}`, background: t.bg, borderRadius: 9,
      padding: '9px 12px', minWidth: 96,
    }}>
      <div style={{ fontSize: 19, fontWeight: 800, color: t.fg, lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: .3, color: t.fg, opacity: .82, marginTop: 2 }}>{label}</div>
    </div>
  );
};

const Notice = ({ tone = 'warning', children }) => {
  const t = TONE[tone] || TONE.warning;
  return (
    <div style={{
      display: 'flex', gap: 9, alignItems: 'flex-start', padding: '10px 12px',
      borderRadius: 8, background: t.bg, border: `1px solid ${t.bd}`,
      color: t.fg, fontSize: 12, lineHeight: 1.55,
    }}>
      <AlertTriangle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
};

const btn = (kind) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 8, font: 'inherit',
  fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
  ...(kind === 'primary' ? {
    border: 0, color: '#fff', background: 'linear-gradient(135deg,#123A6D,#0B2452)',
  } : kind === 'go' ? {
    border: 0, color: '#fff', background: '#1E6B39',
  } : {
    border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkSoft,
  }),
});

export default function NhiaReportPanel() {
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);   // 'preview' | 'import' | 'fix' | 'apply'
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState([]);

  //  The open bulk fix: its rule, the planned rows, and which are ticked.
  const [fix, setFix] = useState(null);
  const [chosen, setChosen] = useState(() => new Set());
  const [fixResult, setFixResult] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const [s, b] = await Promise.all([
        axios.get(nhiaReportSummary),
        axios.get(nhiaReportBatches),
      ]);
      setSummary(s.data?.ok ? s.data : null);
      setBatches(b.data?.batches || []);
    } catch { /* the screen still works without it */ }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const pick = async (f) => {
    if (!f) return;
    setFile(f); setPreview(null); setError(null); setFixResult(null);
    setBusy('preview');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await axios.post(nhiaReportPreview, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(null); }
  };

  const commit = async () => {
    if (!file) return;
    setBusy('import'); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(nhiaReportImport, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview({ ...data, imported: true });
      await loadSummary();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(null); }
  };

  const openFix = async (rule) => {
    setBusy('fix'); setError(null); setFixResult(null);
    try {
      const { data } = await axios.get(nhiaFixPreview, { params: { rule } });
      setFix(data);
      //  Everything the planner would change starts ticked — refused rows are not
      //  selectable at all, so this cannot arm anything the planner rejected.
      setChosen(new Set((data.rows || [])
        .filter((r) => r.action === 'change').map((r) => String(r.claimId))));
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(null); }
  };

  const applyFix = async () => {
    if (!fix || !chosen.size) return;
    setBusy('apply'); setError(null);
    try {
      const { data } = await axios.post(nhiaFixApply, {
        rule: fix.rule, claimIds: [...chosen],
      });
      setFixResult(data);
      setFix(null); setChosen(new Set());
      await loadSummary();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(null); }
  };

  //  ── FIX ALL ──────────────────────────────────────────────────────────────
  //  Every row the planner will change, in one go, without opening the table.
  //
  //  It still ASKS first, and the question names the number and what will happen
  //  to it — because two of these rules alter a patient's name and a membership
  //  number. Sending no claimIds is what tells the server "all of them", and the
  //  server re-plans, so this cannot apply anything the planner refuses.
  const fixAll = async (rule, count, title) => {
    if (!window.confirm(
      `${title}\n\n`
      + `This will change ${count} claim${count === 1 ? '' : 's'} in one pass.\n\n`
      + 'Rows the system will not touch are left alone and reported afterwards. '
      + 'There is no undo.')) return;
    setBusy('apply'); setError(null); setFix(null); setChosen(new Set());
    try {
      //  No claimIds: the server applies every row its own plan marks as a
      //  change, re-read at the moment of writing.
      const { data } = await axios.post(nhiaFixApply, { rule });
      setFixResult(data);
      await loadSummary();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(null); }
  };

  //  The plan's own counts per rule, so a "Fix all" button can say how many it
  //  will touch before it is pressed. Fetched once with the summary.
  const [fixCounts, setFixCounts] = useState({});
  useEffect(() => {
    axios.get(nhiaFixPreview)
      .then(({ data }) => {
        const m = {};
        (data?.fixes || []).forEach((f) => { m[f.rule] = f; });
        setFixCounts(m);
      })
      .catch(() => setFixCounts({}));
    //  Re-read after a fix lands, so the counts do not go stale.
  }, [fixResult]);

  const rules = summary?.rules || [];
  const errorRules = rules.filter((r) => r.severity === 'error');
  const warnRules = rules.filter((r) => r.severity !== 'error');

  return (
    <div style={{ padding: 16, maxWidth: 1180, margin: '0 auto', color: BRAND.ink }}>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>NHIA import report</h2>
        <div style={{ fontSize: 12.5, color: BRAND.inkMuted, marginTop: 4, lineHeight: 1.55 }}>
          Load the HTML report the portal returns after a submission. Every claim
          it rejected will then say why, on the claim itself, in the editor —
          instead of being resubmitted with the same fault.
        </div>
      </div>

      {error && <div style={{ marginBottom: 12 }}>
        <Notice tone="error">{error}</Notice></div>}

      {/* ── 1. the file ───────────────────────────────────────────────────── */}
      <div style={{
        border: `1px dashed ${BRAND.line}`, borderRadius: 11, padding: 16,
        background: '#F8FAFC', marginBottom: 14,
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      }}>
        <input type="file" hidden ref={fileInput}
          accept=".html,.htm,text/html"
          onChange={(e) => pick(e.target.files?.[0])} />
        <button type="button" style={btn('primary')} disabled={!!busy}
          onClick={() => fileInput.current?.click()}>
          {busy === 'preview' ? <Loader size={14} /> : <UploadCloud size={14} />}
          {busy === 'preview' ? 'Reading…' : 'Choose the report'}
        </button>
        <div style={{ flex: 1, minWidth: 200, fontSize: 12, color: BRAND.inkMuted }}>
          {file ? (
            <><FileText size={12} style={{ verticalAlign: -2 }} /> {file.name}
              {' · '}{(file.size / 1024 / 1024).toFixed(1)} MB</>
          ) : 'The ClaimIt import report — an HTML file, usually several megabytes.'}
        </div>
      </div>

      {/* ── 2. what it says, before anything is stored ────────────────────── */}
      {preview && (
        <div style={{
          border: `1px solid ${BRAND.line}`, borderRadius: 11, padding: 14,
          marginBottom: 14, background: '#fff',
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10,
            flexWrap: 'wrap', marginBottom: 11 }}>
            <span style={{ fontSize: 13.5, fontWeight: 800 }}>
              {preview.imported ? 'Imported' : 'This report says'}
            </span>
            <span style={{ fontSize: 11.5, color: BRAND.inkMuted }}>
              {preview.title || file?.name}
            </span>
            {preview.imported && <Pill tone="ok"><CheckCircle2 size={10} /> stored</Pill>}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 11 }}>
            <Stat label="claims" value={preview.totals?.total ?? 0} />
            <Stat label="accepted" value={preview.totals?.saved ?? 0} tone="ok" />
            <Stat label="rejected" value={preview.totals?.error ?? 0} tone="error" />
            <Stat label="queried" value={preview.totals?.warning ?? 0} tone="warning" />
            <Stat label="updated" value={preview.totals?.updated ?? 0} />
          </div>

          {/*  Whether these verdicts will attach to anything. A report from
              another facility matches nothing, and finding that out after storing
              thousands of rows would be a mess to unpick. */}
          {(preview.matched != null) && (
            <div style={{ marginBottom: 11 }}>
              {preview.unmatched > 0 ? (
                <Notice tone="warning">
                  <strong>{preview.unmatched}</strong> of {preview.matched + preview.unmatched}
                  {' '}claims in this report are not in this installation. Their
                  verdicts are still kept, but they will not appear on any claim —
                  check this is the right facility&apos;s report.
                </Notice>
              ) : (
                <div style={{ fontSize: 12, color: '#1E6B39' }}>
                  <CheckCircle2 size={12} style={{ verticalAlign: -2 }} />
                  {' '}All {preview.matched} claims in this report were matched to
                  claims here.
                </div>
              )}
            </div>
          )}

          {preview.unclassified > 0 && (
            <div style={{ marginBottom: 11 }}>
              <Notice tone="warning">
                <strong>{preview.unclassified}</strong> message(s) are ones this
                system has not been taught. They are still shown on their claims,
                word for word — they just carry no explanation. Worth sending on so
                the rule can be added.
              </Notice>
            </div>
          )}

          {!preview.imported && (
            <div style={{ display: 'flex', gap: 9, alignItems: 'center',
              flexWrap: 'wrap' }}>
              <button type="button" style={btn('go')} disabled={!!busy}
                onClick={commit}>
                {busy === 'import' ? <Loader size={14} /> : <CheckCircle2 size={14} />}
                {busy === 'import' ? 'Storing…' : 'Keep these verdicts'}
              </button>
              <span style={{ fontSize: 11.5, color: BRAND.inkMuted, flex: 1,
                minWidth: 200 }}>
                Nothing has been stored yet. Re-importing the same report later is
                safe — it replaces rather than doubles.
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── 3. the rules, and what can be done about them ─────────────────── */}
      {summary?.batch && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10,
            flexWrap: 'wrap', marginBottom: 9 }}>
            <span style={{ fontSize: 13.5, fontWeight: 800 }}>What NHIA reported</span>
            <span style={{ fontSize: 11.5, color: BRAND.inkMuted }}>
              {summary.batch.report_title}
              {batches.length > 1 ? ` · ${batches.length} reports loaded` : ''}
            </span>
          </div>

          {[['Rejections', errorRules, 'error'], ['Queries', warnRules, 'warning']]
            .filter(([, list]) => list.length)
            .map(([heading, list, tone]) => (
              <div key={heading} style={{ marginBottom: 11 }}>
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
                  letterSpacing: .4, color: BRAND.inkMuted, marginBottom: 6 }}>
                  {heading}
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {list.map((r) => {
                    const open = expanded === r.ruleCode;
                    return (
                      <div key={`${r.ruleCode}-${r.severity}`} style={{
                        border: `1px solid ${TONE[tone].bd}`, borderRadius: 9,
                        background: '#fff', overflow: 'hidden',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                          padding: '9px 11px', flexWrap: 'wrap' }}>
                          <span style={{ minWidth: 74, fontSize: 12, color: BRAND.inkMuted }}>
                            <strong style={{ fontSize: 14, color: TONE[tone].fg }}>
                              {r.claims}
                            </strong>
                            {' '}claim{r.claims === 1 ? '' : 's'}
                          </span>
                          <span style={{ flex: 1, minWidth: 190, fontSize: 12.5,
                            fontWeight: 700 }}>{r.title}</span>

                          {r.unmatched > 0 && (
                            <Pill title="these verdicts belong to claims not held here">
                              {r.unmatched} unmatched
                            </Pill>
                          )}

                          {r.fixable ? (
                            <>
                              {/*  ── FIX ALL ────────────────────────────────
                                   One press, every row the planner will change.
                                   It shows the COUNT so the number is known
                                   before it is pressed, and it is absent when
                                   the planner would change nothing — a button
                                   promising to fix 0 claims is worse than no
                                   button. */}
                              {fixCounts[r.ruleCode]?.change > 0 && (
                                <button type="button" style={btn('go')} disabled={!!busy}
                                  title={fixCounts[r.ruleCode]?.note || ''}
                                  onClick={() => fixAll(r.ruleCode,
                                    fixCounts[r.ruleCode].change,
                                    fixCounts[r.ruleCode].title || r.title)}>
                                  <Wand2 size={12} /> Fix all {fixCounts[r.ruleCode].change}
                                </button>
                              )}
                              <button type="button" style={btn('plain')} disabled={!!busy}
                                onClick={() => openFix(r.ruleCode)}>
                                <Wrench size={12} /> Review first
                              </button>
                            </>
                          ) : (
                            <Pill title={r.fixNote || ''}>
                              <Info size={10} /> needs a person
                            </Pill>
                          )}

                          <button type="button" aria-label="Why"
                            onClick={() => setExpanded(open ? null : r.ruleCode)}
                            style={{ border: 0, background: 'none', cursor: 'pointer',
                              color: BRAND.inkMuted, padding: 2 }}>
                            {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                          </button>
                        </div>

                        {open && (
                          <div style={{ borderTop: `1px solid ${BRAND.line}`,
                            padding: '9px 11px', background: '#F8FAFC', fontSize: 11.5,
                            color: BRAND.inkSoft, lineHeight: 1.6 }}>
                            {r.why && <div>{r.why}</div>}
                            {r.fixNote && (
                              <div style={{ marginTop: 5, color: BRAND.inkMuted }}>
                                {r.fixNote}
                              </div>
                            )}
                            {/*  NHIA's own sentence. What an officer quotes if they
                                have to query it. */}
                            <div style={{ marginTop: 6, paddingLeft: 9,
                              borderLeft: `2px solid ${BRAND.line}`, fontSize: 11,
                              color: BRAND.inkMuted }}>{r.example}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

          {!rules.length && (
            <div style={{ fontSize: 12.5, color: '#1E6B39' }}>
              <CheckCircle2 size={13} style={{ verticalAlign: -2 }} />
              {' '}Nothing was rejected or queried in this report.
            </div>
          )}
        </div>
      )}

      {/* ── 4. the bulk fix, as a table you read ──────────────────────────── */}
      {fix && (
        <div style={{
          border: '1px solid rgba(18,58,109,.35)', borderRadius: 11,
          background: '#F7FAFF', padding: 14, marginBottom: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10,
            flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={{ fontSize: 13.5, fontWeight: 800 }}>{fix.title}</span>
            <Pill tone="plain">{fix.counts?.change || 0} to change</Pill>
            {fix.counts?.refuse > 0 && (
              <Pill tone="warning">{fix.counts.refuse} refused</Pill>
            )}
            {fix.counts?.skip > 0 && <Pill tone="ok">{fix.counts.skip} already right</Pill>}
          </div>

          <div style={{ fontSize: 12, color: BRAND.inkSoft, lineHeight: 1.6,
            marginBottom: 10 }}>{fix.note}</div>

          <div style={{ maxHeight: 330, overflow: 'auto', border: `1px solid ${BRAND.line}`,
            borderRadius: 8, background: '#fff' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
              <thead>
                <tr style={{ background: '#F1F5F9' }}>
                  <th style={{ width: 30 }} />
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 800 }}>Claim</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 800 }}>Field</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 800 }}>Now</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 800 }}>Becomes</th>
                </tr>
              </thead>
              <tbody>
                {(fix.rows || []).map((r, i) => {
                  const change = r.action === 'change';
                  const key = String(r.claimId);
                  const on = chosen.has(key);
                  return (
                    <tr key={`${key}-${r.medId || r.field}-${i}`}
                      style={{
                        borderTop: `1px solid ${BRAND.line}`,
                        background: change ? '#fff' : '#F8FAFC',
                        opacity: change ? 1 : .75,
                      }}>
                      <td style={{ padding: '5px 8px' }}>
                        {/*  A refused row cannot be ticked — the planner has
                            already said it will not do it, and offering a
                            checkbox would imply otherwise. */}
                        {change ? (
                          <input type="checkbox" checked={on}
                            onChange={() => setChosen((s) => {
                              const n = new Set(s);
                              if (n.has(key)) n.delete(key); else n.add(key);
                              return n;
                            })} />
                        ) : <span style={{ color: BRAND.inkFaint }}>—</span>}
                      </td>
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{r.ref}</td>
                      <td style={{ padding: '5px 8px', color: BRAND.inkMuted }}>
                        {r.label || r.field}
                      </td>
                      <td style={{ padding: '5px 8px', fontFamily: 'inherit' }}>
                        {r.from === null || r.from === '' ? '(blank)' : String(r.from)}
                      </td>
                      <td style={{ padding: '5px 8px' }}>
                        {change ? (
                          <strong style={{ color: '#1E6B39' }}>{String(r.to)}</strong>
                        ) : (
                          <span style={{ color: '#7A4A00' }}>
                            {r.action === 'skip' ? 'already correct' : 'left alone'}
                            {r.why ? ` — ${r.why}` : ''}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 11,
            flexWrap: 'wrap' }}>
            <button type="button" style={btn('go')}
              disabled={!!busy || !chosen.size} onClick={applyFix}>
              {busy === 'apply' ? <Loader size={14} /> : <Wrench size={14} />}
              {busy === 'apply' ? 'Applying…'
                : `Apply to ${chosen.size} claim${chosen.size === 1 ? '' : 's'}`}
            </button>
            <button type="button" style={btn('plain')} disabled={!!busy}
              onClick={() => { setFix(null); setChosen(new Set()); }}>Cancel</button>
            <span style={{ fontSize: 11.5, color: BRAND.inkMuted, flex: 1, minWidth: 190 }}>
              Re-checked on the server before anything is written, so a stale
              table cannot apply an out-of-date change.
            </span>
          </div>
        </div>
      )}

      {/*  What actually happened, broken down. "11 changed" would hide that this
           rule made TWO different corrections to members' identifiers, so each is
           named with its own count — and every row left alone is listed with the
           reason, because those are still outstanding. */}
      {fixResult && (
        <div style={{
          border: `1px solid ${TONE[fixResult.failed ? 'error' : 'ok'].bd}`,
          background: TONE[fixResult.failed ? 'error' : 'ok'].bg,
          borderRadius: 10, padding: 13, marginBottom: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8,
            flexWrap: 'wrap' }}>
            <CheckCircle2 size={15} style={{ color: TONE[fixResult.failed ? 'error' : 'ok'].fg }} />
            <strong style={{ fontSize: 13 }}>{fixResult.title}</strong>
            <span style={{ fontSize: 12.5, color: BRAND.inkSoft }}>
              {fixResult.changed} claim{fixResult.changed === 1 ? '' : 's'} changed
              {fixResult.failed ? `, ${fixResult.failed} failed` : ''}
            </span>
          </div>

          {fixResult.byKind && Object.keys(fixResult.byKind).length > 0 && (
            <ul style={{ margin: '7px 0 0 20px', padding: 0, fontSize: 12,
              color: BRAND.inkSoft, lineHeight: 1.7 }}>
              {Object.entries(fixResult.byKind).map(([kind, n]) => (
                <li key={kind}>
                  <strong>{n}</strong>{' '}
                  {kind === 'move_card'
                    ? 'Ghana Card number moved out of the membership field into the '
                      + 'Ghana Card field'
                    : kind === 'letter_o'
                      ? 'letter O corrected to a zero in the membership number'
                      : kind === 'other_names'
                        ? 'patient name with the digit spelled out'
                        : kind === 'includesPharmacy'
                          ? 'pharmacy flag cleared'
                          : kind === 'dose_unit' ? 'dose unit filled in'
                            : kind === 'dose' ? 'dose rewritten as VALUE UNIT'
                              : `changed (${kind})`}
                </li>
              ))}
            </ul>
          )}

          {fixResult.refusals?.length > 0 && (
            <div style={{ marginTop: 9, fontSize: 12, color: '#7A4A00' }}>
              <strong>{fixResult.refused} left alone</strong> — these need a person:
              <ul style={{ margin: '4px 0 0 20px', padding: 0, lineHeight: 1.6 }}>
                {fixResult.refusals.slice(0, 6).map((r, i) => (
                  <li key={`${r.ref}-${i}`}>
                    {r.ref}: <code>{String(r.from ?? '(blank)')}</code> — {r.why}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ marginTop: 9, fontSize: 11.5, color: BRAND.inkMuted }}>
            Re-export the month and the corrected claims will go out clean.
          </div>
        </div>
      )}

      {/* ── what has been loaded before ───────────────────────────────────── */}
      {batches.length > 0 && (
        <div style={{ borderTop: `1px solid ${BRAND.line}`, paddingTop: 11 }}>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
            letterSpacing: .4, color: BRAND.inkMuted, marginBottom: 6 }}>
            Reports loaded
          </div>
          <div style={{ display: 'grid', gap: 4 }}>
            {batches.slice(0, 8).map((b) => (
              <div key={b.id} style={{ display: 'flex', gap: 10, fontSize: 11.5,
                color: BRAND.inkSoft, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <span style={{ minWidth: 140, color: BRAND.inkMuted }}>
                  {String(b.reported_at || b.imported_at || '').slice(0, 16)}
                </span>
                <span style={{ flex: 1, minWidth: 180 }}>{b.report_title || b.filename}</span>
                <span>{b.claims_total} claims</span>
                <span style={{ color: '#A62828' }}>{b.claims_error} rejected</span>
                <span style={{ color: '#7A4A00' }}>{b.claims_warning} queried</span>
                <span style={{ color: BRAND.inkMuted }}>{b.verdicts} verdicts</span>
              </div>
            ))}
          </div>
          <button type="button" style={{ ...btn('plain'), marginTop: 9 }}
            onClick={loadSummary}>
            <RotateCcw size={12} /> Refresh
          </button>
        </div>
      )}

      {!summary?.batch && !preview && (
        <div style={{ padding: '26px 0', textAlign: 'center', color: BRAND.inkMuted,
          fontSize: 12.5, lineHeight: 1.6 }}>
          <XCircle size={18} style={{ opacity: .4 }} />
          <div style={{ marginTop: 6 }}>
            No report has been loaded yet. Choose one above and nothing is stored
            until you say so.
          </div>
        </div>
      )}
    </div>
  );
}
